/** @jsx jsx */
import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { Button, Card, Classes, Dialog, H4, H6, Icon, Intent } from '@blueprintjs/core';
import { css, jsx } from '@emotion/react';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WIDGETS } from 'widgets';
import { ExplorerCard } from 'views/explorer/explorerCard';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import isEqual from 'lodash-es/isEqual';
import { ColorAliases } from 'styles/variables';
import CardContext from './cardContext';
import { ColorScalesContext } from 'views/common/colorScalesContext';

const DIALOG_VIEWS = {
	SETTINGS: 'SETTINGS',
	CANCEL: 'CANCEL',
};

export default function ({ isOpen, close, card, cardIndex }) {
	const [viewState, setViewState] = useState(DIALOG_VIEWS.SETTINGS);
	const [closeFlag, setCloseFlag] = useState(false);
	const originalEndpointRef = useRef();
	const originalConfigRef = useRef();
	const { dispatch } = useContext(WorkspaceContext);

	useEffect(() => {
		if (isOpen) {
			//cache original state in case user clicks "cancel"
			originalEndpointRef.current = JSON.parse(JSON.stringify(card.endpointQuery));
			//if widgetConfig defined then make a 'deep' copy; if not defined just use it as is (thus all equality checks will pass)
			originalConfigRef.current = card.widgetConfiguration
				? JSON.parse(JSON.stringify(card.widgetConfiguration))
				: card.widgetConfiguration;
		} else {
			//set things back up for when button is pressed again
			setViewState(DIALOG_VIEWS.SETTINGS);
		}
	}, [isOpen]);

	const _prepCancel = () => {
		if (
			!isEqual(originalEndpointRef.current, card.endpointQuery) ||
			!isEqual(originalConfigRef.current, card.widgetConfiguration)
		) {
			setViewState(DIALOG_VIEWS.CANCEL);
		} else {
			close();
		}
	};

	//In the cancel dialog there are two things that can happen. 1. Go back to setting done with setViewState, or cancel settings. Which is handled here
	const _cancelSettings = () => {
		dispatch(WorkspaceActionCreator.options(cardIndex, originalEndpointRef.current));
		dispatch(WorkspaceActionCreator.configuration(cardIndex, originalConfigRef.current));
		//Need to wait till original data is put in place, then we can close (other wise widget does a quick render of altered data then again with original data)
		setCloseFlag(true);
	};
	useEffect(() => {
		if (closeFlag) {
			close();
			setCloseFlag(false);
		}
		// Don't listen on closeFlag, otherwise it will fire too soon. Wait for the card updates and know closeFlag will be what it should be
	}, [card.endpointQuery, card.widgetConfiguration /*, closeFlag*/]);

	const value = useMemo(
		() => ({
			isSettingsDialogOpen: !isOpen, //fake out the widget in the dialog to update only when dialog is open
		}),
		[isOpen]
	);

	return (
		<React.Fragment>
			<CardContext.Provider value={value}>
				<Settings
					isOpen={isOpen && viewState === DIALOG_VIEWS.SETTINGS}
					onSave={close}
					onCancel={_prepCancel}
					card={card}
					cardIndex={cardIndex}
				/>
				<CancelConfirm
					isOpen={isOpen && viewState === DIALOG_VIEWS.CANCEL}
					close={_cancelSettings}
					card={card}
					cardIndex={cardIndex}
					setViewState={setViewState}
				/>
			</CardContext.Provider>
		</React.Fragment>
	);
}

function Settings({ isOpen, onSave, onCancel, card, cardIndex }) {
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult },
		},
		dispatch,
	} = useContext(WorkspaceContext);
	let { widgetConfiguration, endpointQuery, type } = card;
	const { Knobs, flags, types, label } = WIDGETS[type];

	const colorScaleFunction = useContext(ColorScalesContext);
	if (widgetConfiguration) {
		//if this app specifies this widgets colors use those, otherwise widget specific scales (if not set then later on will use default)
		let availableColorScales =
			colorScaleFunction?.getAvailableScales(type) || widgetConfiguration?.availableColorScales;
		widgetConfiguration = { availableColorScales, ...widgetConfiguration };
	}

	return (
		<Dialog isOpen={isOpen} css={dialogRoot}>
			<div className="bp3-dialog-header">
				<h4 className="bp3-heading">{label} Settings</h4>
				<Button
					cy-test="settings-dialog-close-button"
					text={'Close'}
					minimal={true}
					css={cancelButton}
					onClick={onCancel}
				/>
				<Button
					cy-test="settings-dialog-save-button"
					text={'Save'}
					outlined={true}
					intent={Intent.PRIMARY}
					onClick={onSave}
				/>
			</div>
			<div className={Classes.DIALOG_BODY} css={dialogBody}>
				<Card css={knobCard}>
					<Knobs
						cardIndex={cardIndex}
						endpointQuery={endpointQuery}
						widgetConfiguration={widgetConfiguration}
						flags={flags}
						types={types}
					/>
				</Card>
				<Card css={widgetCard}>
					<H6>Preview</H6>
					<ExplorerCard
						stopResize={false}
						dispatch={dispatch}
						cardIndex={cardIndex}
						card={card}
						selectedDefinitionName={selectedDefinitionName}
						result={activeResult}
					/>
				</Card>
			</div>
		</Dialog>
	);
}
const dialogRoot = css`
	padding-bottom: 0;
	min-width: 70%;
	overflow: hidden;
	height: 60vh; //min-height causes window to infinitely grow
`;

const dialogBody = css`
	display: grid;
	grid-template-columns: 0.55fr 0.45fr;
	grid-column-gap: 8px;
	margin: 8px 8px 9px 7px;
	overflow: hidden;
`;
const knobCard = css`
	overflow: auto;
`;
const widgetCard = css`
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;
const cancelButton = css`
	margin-right: 5px;
`;

function CancelConfirm({ isOpen, setViewState, close }) {
	const _return = () => {
		setViewState(DIALOG_VIEWS.SETTINGS);
	};

	return (
		<Dialog isOpen={isOpen} css={cancelDialogCss}>
			<div className="bp3-dialog-header">
				<Icon
					icon={'warning-sign'}
					css={css`
						color: ${ColorAliases.PT_INTENT_WARNING} !important;
					`}
				/>
				<H4 className="bp3-heading">Cancel Changes</H4>
			</div>
			<div className={Classes.DIALOG_BODY}>
				Changes have been made to the widget settings. Closing will revert settings to their most recent state.
			</div>
			<div className={Classes.DIALOG_FOOTER}>
				<div className={Classes.DIALOG_FOOTER_ACTIONS}>
					<Button
						cy-test="cancel-confirm-return-button"
						text={'Return to Settings'}
						minimal={true}
						css={cancelButton}
						onClick={_return}
					/>
					<Button
						cy-test="cancel-confirm-cancel-button"
						text={'Cancel Changes'}
						outlined={true}
						intent={Intent.PRIMARY}
						onClick={close}
					/>
				</div>
			</div>
		</Dialog>
	);
}

const cancelDialogCss = css`
	background: ${ColorAliases.PT_APP_BACKGROUND_COLOR};
`;
